import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  NewsContent,
  NewsImageItem,
  NewsItem,
  NewsRecipient,
  NewsRecipientReading,
  NewsRef,
  NewsType,
} from '../../../model/v3/news';
import { V3FileItem } from '../../../model/v3/file';
import { Page, Pageable } from '../../../model/rest/base';
import { appendIfDefined, createPageableParams } from './rest-v3-utils';

@Injectable({
  providedIn: 'root',
})
export class AdminNewsV3RestService {
  constructor(private http: HttpClient) {}

  private baseUrl(path: string = ''): string {
    return `${environment.eventsApiBase}/v1/news${path}`;
  }

  listNews(search?: string | null, type?: NewsType | null, pageable?: Pageable): Observable<Page<NewsItem>> {
    let params = appendIfDefined(createPageableParams(pageable), 'search', search);
    params = appendIfDefined(params, 'type', type);
    return this.http.get<Page<NewsItem>>(this.baseUrl(), { params });
  }

  getNewsById(newsId: number): Observable<NewsItem> {
    return this.http.get<NewsItem>(this.baseUrl(`/${newsId}`));
  }

  createNews(content: NewsContent): Observable<NewsRef> {
    return this.http.post<NewsRef>(this.baseUrl(), content);
  }

  updateNewsContent(newsId: number, content: NewsContent): Observable<NewsItem> {
    return this.http.put<NewsItem>(this.baseUrl(`/${newsId}`), content);
  }

  updateNewsImage(newsId: number, imageFile: V3FileItem | null): Observable<void> {
    return this.http.put<void>(this.baseUrl(`/${newsId}/image`), imageFile);
  }

  addNewsImage(newsId: number, imageFile: V3FileItem): Observable<NewsImageItem> {
    return this.http.post<NewsImageItem>(this.baseUrl(`/${newsId}/images`), imageFile);
  }

  deleteNewsImage(newsId: number, imageId: number): Observable<void> {
    return this.http.delete<void>(this.baseUrl(`/${newsId}/images/${imageId}`));
  }

  // body: all image ids of the news in the desired display order
  updateNewsImagesOrder(newsId: number, orderedImageIds: number[]): Observable<NewsImageItem[]> {
    return this.http.put<NewsImageItem[]>(this.baseUrl(`/${newsId}/images`), orderedImageIds);
  }

  updatePublicationDate(newsId: number, publicationDate: Date | string): Observable<void> {
    const normalizedDate = typeof publicationDate === 'string'
      ? new Date(publicationDate).toISOString()
      : publicationDate.toISOString();
    return this.http.patch<void>(
      this.baseUrl(`/${newsId}`),
      JSON.stringify(normalizedDate),
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      },
    );
  }

  deleteNews(newsId: number): Observable<void> {
    return this.http.delete<void>(this.baseUrl(`/${newsId}`));
  }

  listRecipients(newsId: number, pageable?: Pageable): Observable<Page<NewsRecipient>> {
    const params = createPageableParams(pageable);
    return this.http.get<Page<NewsRecipient>>(this.baseUrl(`/${newsId}/recipients`), { params });
  }

  listRecipientsReadingDate(
    newsId: number,
    groupBySchool?: boolean | null,
    pageable?: Pageable,
  ): Observable<Page<NewsRecipientReading>> {
    const params = appendIfDefined(createPageableParams(pageable), 'groupBySchool', groupBySchool);
    return this.http.get<Page<NewsRecipientReading>>(
      this.baseUrl(`/${newsId}/recipients/reading-date`),
      { params },
    );
  }

  updateRecipients(newsId: number, recipients: NewsRecipient[]): Observable<void> {
    return this.http.put<void>(this.baseUrl(`/${newsId}/recipients`), recipients);
  }
}
